import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { BadgeCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DirShell, useGate } from "@/components/directory/DirShell";
import { Stars } from "@/components/directory/Stars";
import { FollowButton } from "@/components/directory/ShopCard";

const FollowBox = ({ slug }) => {
  const [s, setS] = useState(null);
  const { user } = useAuth();
  useEffect(() => { api.get(`/api/dir/shops/${slug}`).then(r => setS(r.data)).catch(() => {}); }, [slug, user]);
  if (!s || s.can_manage) return null;
  return (
    <section className="bg-slate-900 text-white rounded-3xl p-6 flex flex-wrap items-center gap-4" data-testid="review-invite-follow">
      <div className="flex-1 min-w-[200px]"><p className="font-bold">Follow us on ShopAdvisor</p><p className="text-sm text-white/60">Get {s.name}'s specials and updates in your feed.</p></div>
      {<FollowButton s={s} onChange={(d) => setS({ ...s, ...d })} className="h-10 px-5" />}
      <Link to={`/directory/shop/${slug}`} className="text-sm font-bold text-amber-400 hover:text-amber-300" data-testid="review-invite-shop-link">View our page</Link>
    </section>
  );
};

const Inner = () => {
  const { token } = useParams();
  const { user } = useAuth();
  const { need } = useGate();
  const [inv, setInv] = useState(null);
  const [stars, setStars] = useState(0);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  useEffect(() => { api.get(`/api/dir/review-invite/${token}`).then(r => setInv(r.data)).catch(() => setInv(false)); }, [token]);
  const send = () => need(async () => {
    if (!stars) return toast.error("Pick a star rating");
    setBusy(true);
    try { const { data } = await api.post(`/api/dir/review-invite/${token}`, { stars, text }); setDone(data.slug); toast.success("Thanks — your verified review is live"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  });
  return (
    <div className="max-w-xl mx-auto px-4 py-12" data-testid="review-invite-page">
      {inv === null && <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />}
      {inv === false && <p className="text-center text-slate-500" data-testid="review-invite-invalid">This review link isn't valid.</p>}
      {inv && <section className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 space-y-4">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 flex items-center gap-1"><BadgeCheck className="w-4 h-4" />Verified customer review</p>
        <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight" data-testid="review-invite-shop">How was {inv.shop.name}?</h1>
        <p className="text-sm text-slate-600">Hi {inv.customer_name.split(" ")[0] || "there"}{inv.vehicle && ` — thanks for bringing in your ${inv.vehicle}`}. Your review gets a Verified customer badge because it's tied to your visit.</p>
        {done || (inv.used && !user) ? <div className="space-y-3"><p className="font-bold text-green-700" data-testid="review-invite-done">{done ? "Your review is posted. Thank you!" : "A review was already left with this link."}</p><Link to={`/directory/shop/${done || inv.shop.slug}?tab=reviews`} className="inline-block h-10 px-5 leading-10 rounded-full bg-slate-900 text-white text-sm font-bold" data-testid="review-invite-view">See the shop's page</Link></div> : <>
          <Stars value={stars} size="w-9 h-9" onPick={setStars} testId="invite-star" />
          <textarea value={text} onChange={e => setText(e.target.value)} rows={4} maxLength={3000} placeholder="How was the service, price and communication?" className="w-full rounded-xl border border-slate-200 p-3 text-sm focus:outline-none focus:border-blue-500" data-testid="review-invite-text" />
          <button disabled={busy} onClick={send} className="h-11 px-6 rounded-full bg-slate-900 text-white font-bold text-sm flex items-center gap-2 disabled:opacity-50" data-testid="review-invite-submit">{busy && <Loader2 className="w-4 h-4 animate-spin" />}{user ? "Post my review" : "Sign in or create an account to post"}</button>
          <p className="text-xs text-slate-400">Your screen name shows with the review — never your email or phone.</p></>}
      </section>}
      {inv && <div className="mt-5"><FollowBox slug={inv.shop.slug} /></div>}
    </div>
  );
};

export default function ReviewInvitePage() {
  return <DirShell><Inner /></DirShell>;
}
